import { useState, useCallback, useRef } from 'react';

interface CartLine {
  id: string;
  merchandiseId: string;
  quantity: number;
  merchandise: {
    id: string;
    title: string;
    product: {
      title: string;
      featuredImage?: {
        url: string;
      };
    };
    priceV2: {
      amount: string;
      currencyCode: string;
    };
  };
}

interface Cart {
  id: string;
  checkoutUrl: string;
  lines: {
    edges: Array<{
      node: CartLine;
    }>;
  };
  cost: {
    totalAmount: {
      amount: string;
      currencyCode: string;
    };
    subtotalAmount: {
      amount: string;
      currencyCode: string;
    };
  };
}

const SUPABASE_URL = import.meta.env.VITE_PUBLIC_SUPABASE_URL;
const SUPABASE_ANON_KEY = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY;

const CART_LINES_FRAGMENT = `
  id
  checkoutUrl
  lines(first: 50) {
    edges {
      node {
        id
        quantity
        merchandise {
          ... on ProductVariant {
            id
            title
            product {
              title
              featuredImage {
                url
              }
            }
            priceV2 {
              amount
              currencyCode
            }
          }
        }
      }
    }
  }
  cost {
    totalAmount {
      amount
      currencyCode
    }
    subtotalAmount {
      amount
      currencyCode
    }
  }
`;

export const useCart = () => {
  const [cart, setCart] = useState<Cart | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Store cart id in a ref so callbacks don't need cart in their dep arrays
  const cartIdRef = useRef<string | null>(null);

  const shopifyFetch = useCallback(async (query: string, variables: Record<string, unknown> = {}) => {
    const response = await fetch(`${SUPABASE_URL}/functions/v1/shopify-cart`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
      },
      body: JSON.stringify({ query, variables }),
    });

    const json = await response.json();
    if (json.errors) {
      throw new Error(json.errors[0].message);
    }
    return json.data;
  }, []);

  const createCart = useCallback(async (merchandiseId: string, quantity: number = 1) => {
    setLoading(true);
    setError(null);
    try {
      const mutation = `
        mutation cartCreate($input: CartInput!) {
          cartCreate(input: $input) {
            cart { ${CART_LINES_FRAGMENT} }
          }
        }
      `;

      const data = await shopifyFetch(mutation, {
        input: {
          lines: [{ merchandiseId, quantity }],
          // Buyer identity enables Shop Pay & customer account recognition
          buyerIdentity: { countryCode: 'US' },
          // Return-to URL so post-purchase links back to kenonlyy.com
          attributes: [{ key: 'return_to', value: window.location.origin }],
        },
      });

      const newCart = data.cartCreate.cart;
      setCart(newCart);
      cartIdRef.current = newCart.id;
      localStorage.setItem('shopify_cart_id', newCart.id);
      return newCart;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create cart');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [shopifyFetch]);

  const addToCart = useCallback(async (merchandiseId: string, quantity: number = 1) => {
    setLoading(true);
    setError(null);
    try {
      const cartId = cartIdRef.current || localStorage.getItem('shopify_cart_id');

      if (!cartId) {
        return await createCart(merchandiseId, quantity);
      }

      const mutation = `
        mutation cartLinesAdd($cartId: ID!, $lines: [CartLineInput!]!) {
          cartLinesAdd(cartId: $cartId, lines: $lines) {
            cart { ${CART_LINES_FRAGMENT} }
          }
        }
      `;

      const data = await shopifyFetch(mutation, {
        cartId,
        lines: [{ merchandiseId, quantity }],
      });

      const updatedCart = data.cartLinesAdd.cart;
      setCart(updatedCart);
      cartIdRef.current = updatedCart.id;
      return updatedCart;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add to cart');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [createCart, shopifyFetch]);

  const removeFromCartMutation = useCallback(async (cartId: string, lineId: string) => {
    const mutation = `
      mutation cartLinesRemove($cartId: ID!, $lineIds: [ID!]!) {
        cartLinesRemove(cartId: $cartId, lineIds: $lineIds) {
          cart { ${CART_LINES_FRAGMENT} }
        }
      }
    `;
    const data = await shopifyFetch(mutation, { cartId, lineIds: [lineId] });
    return data.cartLinesRemove.cart;
  }, [shopifyFetch]);

  const updateCartLine = useCallback(async (lineId: string, quantity: number) => {
    const cartId = cartIdRef.current;
    if (!cartId) return;

    setLoading(true);
    setError(null);
    try {
      // Remove line when quantity reaches 0
      if (quantity <= 0) {
        const updatedCart = await removeFromCartMutation(cartId, lineId);
        setCart(updatedCart);
        return;
      }

      const mutation = `
        mutation cartLinesUpdate($cartId: ID!, $lines: [CartLineUpdateInput!]!) {
          cartLinesUpdate(cartId: $cartId, lines: $lines) {
            cart { ${CART_LINES_FRAGMENT} }
          }
        }
      `;

      const data = await shopifyFetch(mutation, {
        cartId,
        lines: [{ id: lineId, quantity }],
      });

      setCart(data.cartLinesUpdate.cart);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update cart');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [shopifyFetch, removeFromCartMutation]);

  const removeFromCart = useCallback(async (lineId: string) => {
    const cartId = cartIdRef.current;
    if (!cartId) return;

    setLoading(true);
    setError(null);
    try {
      const updatedCart = await removeFromCartMutation(cartId, lineId);
      setCart(updatedCart);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to remove from cart');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [removeFromCartMutation]);

  // fetchCart has NO dependency on `cart` — avoids infinite re-fetch loop
  const fetchCart = useCallback(async () => {
    const cartId = localStorage.getItem('shopify_cart_id');
    if (!cartId) return;

    setLoading(true);
    try {
      const query = `
        query getCart($cartId: ID!) {
          cart(id: $cartId) { ${CART_LINES_FRAGMENT} }
        }
      `;

      const data = await shopifyFetch(query, { cartId });
      if (data.cart) {
        setCart(data.cart);
        cartIdRef.current = data.cart.id;
      } else {
        // Cart expired — clear stale ID
        localStorage.removeItem('shopify_cart_id');
      }
    } catch (err) {
      localStorage.removeItem('shopify_cart_id');
      setError(err instanceof Error ? err.message : 'Failed to fetch cart');
    } finally {
      setLoading(false);
    }
  }, [shopifyFetch]); // ← No `cart` dep — safe

  return {
    cart,
    loading,
    error,
    addToCart,
    updateCartLine,
    removeFromCart,
    fetchCart,
  };
};
